import { useMemo, useRef } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import { Fade } from "react-awesome-reveal";
import { withTranslation } from "react-i18next";

// Imports => Config
import config from "@config";

import {
  AcColumn,
  AcContainer,
  AcIcon,
  AcRow,
  AcSaveState,
  ENVIRONMENTS,
  ICONS,
  KEYS,
  SIZES,
  THEMES,
  TYPES,
  VARIANTS,
  VISUALS,
} from "@opp/ui-kit";

// Imports => Hooks
import { useUIActions } from "@hooks";

// Imports => Utilities
import { AcSanitize, AcIsSet, AcIsObject, AcIsEmptyString } from "@opp/ui-kit";

// Imports => Molecules
import AcLeaveFeedbackModal from "@molecules/ac-leave-feedback-modal/ac-leave-feedback-modal";

// Imports => Atoms
import {
  AcAvatarCard,
  AcNavigationList,
  AcHeading,
  AcIconButton,
  AcButton,
  AcLoaderInline,
} from "@opp/ui-kit";

import { setLocale } from "@config/moment";
import { useAppDispatch, useAppSelector } from "@src/rtk/hooks";
import { handleLogout } from "@src/rtk/slices/authSlice";
import { useQueryParam } from "use-query-params";

const AcSubNavigation = ({ store, t, i18n }) => {
  const { profile, ui, settings } = store;
  const dispatch = useAppDispatch();
  const [, setLng] = useQueryParam<string>("lng");
  const isAuthorized = useAppSelector((state) => state.auth);
  const { current_merchant_profiles, is_loading } = profile;
  const { has_unverified_compliance_requirement, configurations } = settings;

  const $ref = useRef(null);

  const { handleClose } = useUIActions(ui, KEYS.SUB_NAVIGATION, $ref);

  const hasUnverifiedComplianceRequirement = useMemo(() => {
    if (!AcIsSet(has_unverified_compliance_requirement)) {
      return false;
    }

    return has_unverified_compliance_requirement;
  }, [has_unverified_compliance_requirement]);

  const handleDisplayLeaveFeedbackModal = async (event) => {
    if (!isAuthorized) {
      return;
    }

    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("leave-feedback", "Leave feedback", { ns: "titles" })}`,
      name: "leave-feedback-modal",
      body: (
        <AcLeaveFeedbackModal
          submit={() => new Promise((resolve) => resolve())}
        />
      ),
      closeable: true,
      visible: true,

      callback: async () => {
        await ui.reset(KEYS.MODAL);
      },
    });
  };

  const logout = (event) => {
    console.log("logout");
    dispatch(handleLogout());
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    if (!isAuthorized) {
      return;
    }
  };

  const getName = useMemo(() => {
    if (
      !AcIsSet(current_merchant_profiles) ||
      !AcIsSet(current_merchant_profiles.merchant)
    ) {
      return null;
    }

    return AcSanitize(current_merchant_profiles.merchant.name);
  }, [current_merchant_profiles]);

  const renderSecurityNavigation = useMemo(() => {
    return (
      <AcNavigationList
        items={[
          {
            type: TYPES.LINK,
            tag: "a",
            href: `${config.pkce.accounts_uri}/dashboard/account-and-security`,
            label: `${t("account-and-security", "Account & security", {
              ns: "subNavigation",
            })}`,
            icon: ICONS.VERIFIED,
            attributes: {
              target: "_blank",
            },
          },
          {
            type: TYPES.LINK,
            tag: "a",
            href: `${config.pkce.accounts_uri}/dashboard/devices-and-sessions`,
            label: `${t("devices-and-session", "Devices & sessions", {
              ns: "subNavigation",
            })}`,
            icon: ICONS.ACCOUNT_DETAILS_OUTLINE,
            attributes: {
              target: "_blank",
            },
          },
        ]}
        role={"menubar"}
        itemScope
        itemType={"http://www.schema.org/SiteNavigationElement"}
      />
    );
  }, [i18n.resolvedLanguage]);

  const renderServiceNavigation = useMemo(() => {
    return (
      <AcNavigationList
        items={[
          {
            type: TYPES.BUTTON,
            theme: THEMES.DANGER,
            label: `${t("log-out", "Log out", { ns: "subNavigation" })}`,
            icon: ICONS.LOGOUT_VARIANT,
            callback: logout,
          },
        ]}
        role={"menubar"}
        itemScope
        itemType={"http://www.schema.org/SiteNavigationElement"}
      />
    );
  }, [logout]);

  const getCurrentLanguage = () => {
    const lang = i18n.resolvedLanguage;
    if (lang !== "cimode") {
      AcSaveState(KEYS.LOCALE, lang);
      setLocale(lang);
    }
    return lang;
  };

  const renderSupportNavigation = useMemo(() => {
    let lang = i18n.resolvedLanguage;

    let support_uri = "https://www.onlinepaymentplatform.com/en/support";

    if (lang) {
      if (lang.startsWith("fr")) {
        support_uri = "https://www.onlinepaymentplatform.com/fr/support";
      }

      if (lang.startsWith("de")) {
        support_uri = "https://www.onlinepaymentplatform.com/de/support";
      }

      if (lang.startsWith("nl")) {
        support_uri = "https://www.onlinepaymentplatform.com/nl/support";
      }
    }

    if (
      AcIsSet(current_merchant_profiles) &&
      AcIsSet(current_merchant_profiles.partner)
    ) {
      const { partner } = current_merchant_profiles;

      if (
        AcIsSet(partner.urls) &&
        AcIsSet(partner.urls.support) &&
        !AcIsEmptyString(partner.urls.support)
      ) {
        support_uri = partner.urls.support;
      }
    }

    return (
      <AcNavigationList
        items={[
          {
            type: TYPES.LINK,
            tag: "a",
            href: support_uri,
            label: `${t("support", "Support", { ns: "subNavigation" })}`,
            icon: ICONS.SUPPORT_AGENT,
            attributes: {
              target: "_blank",
            },
          },
          // {
          //     type: TYPES.BUTTON,
          //     label: `${t('leave-feedback', 'Leave feedback', {ns: 'sub-navigation')}`,
          //     icon: ICONS.MESSAGE_PROCESSING_OUTLINE,
          //     callback: (event) => handleDisplayLeaveFeedbackModal(event),
          // },
          // {
          //     type: TYPES.BUTTON,
          //     label: `${t('keyboard-shortcuts', 'Keyboard shortcuts', {ns: 'subNavigation'})}`,
          //     icon: ICONS.KEYBOARD_OUTLINE,
          //     className: 'h-hidden-xxs h-hidden-xs h-hidden-sm',
          //     callback: (event) => {
          //         const showKeyboardShortcutsEvent = new CustomEvent(
          //             'showKeyboardShortcuts'
          //         );
          //         document.dispatchEvent(showKeyboardShortcutsEvent);
          //     },
          // },
        ]}
        role={"menubar"}
        itemScope
        itemType={"http://www.schema.org/SiteNavigationElement"}
      />
    );
  }, [current_merchant_profiles, handleDisplayLeaveFeedbackModal, i18n]);

  const showTranslationKeyOptions = () => {
    const { environment } = config;

    if (!AcIsSet(environment)) {
      return false;
    }

    switch (environment) {
      case ENVIRONMENTS.DEV:
      case ENVIRONMENTS.DEVELOPMENT:
      case ENVIRONMENTS.TEST:
      case ENVIRONMENTS.LOCAL:
        return true;
      case ENVIRONMENTS.ACCEPT:
      case ENVIRONMENTS.STAGING:
      case ENVIRONMENTS.SANDBOX:
      default:
        return false;
    }
  };
  const changeLanguage = (language) => {
    handleChangeLanguage(language);
    setLng(undefined);
  };

  const renderLanguageFlags = useMemo(() => {
    return (
      <AcRow>
        <AcColumn className={"h-margin-top-10"} xs={12}>
          <img
            onClick={() => changeLanguage("nl-NL")}
            className={`h-cursor-pointer ${
              getCurrentLanguage() === "nl-NL" ? "" : "h-opacity-40"
            }`}
            src={VISUALS.FLAGS.NL}
          />
          <img
            onClick={() => changeLanguage("en-GB")}
            className={`h-margin-left-10 h-cursor-pointer ${
              getCurrentLanguage() === "en-GB" ? "" : "h-opacity-40"
            }`}
            src={VISUALS.FLAGS.EN}
          />
          <img
            onClick={() => changeLanguage("de-DE")}
            className={`h-margin-left-10 h-cursor-pointer ${
              getCurrentLanguage() === "de-DE" ? "" : "h-opacity-40"
            }`}
            src={VISUALS.FLAGS.DE}
          />
          <img
            onClick={() => changeLanguage("fr-FR")}
            className={`h-margin-left-10 h-cursor-pointer ${
              getCurrentLanguage() === "fr-FR" ? "" : "h-opacity-40"
            }`}
            src={VISUALS.FLAGS.FR}
          />
          {showTranslationKeyOptions() ? (
            <AcIcon
              className={`${
                getCurrentLanguage() === "cimode" ? "" : "h-opacity-40"
              }`}
              style={{ top: "-8px", fontSize: "2.2rem" }}
              onClick={() => handleChangeLanguage("cimode")}
              icon={ICONS.NINJA}
            />
          ) : (
            ""
          )}
        </AcColumn>
      </AcRow>
    );
  }, [getCurrentLanguage]);

  const handleChangeLanguage = (language) => {
    i18n.changeLanguage(language);
    if (language !== "cimode") {
      AcSaveState(KEYS.LOCALE, language);
      setLocale(language);

      profile
        .update(t, { language: i18n.resolvedLanguage.slice(0, 2) })
        .then(() => {
          // Todo check if we can do this without hard refresh
          setTimeout(() => {
            window.location.reload(false);
          }, 1000);
        });
    }
  };

  const renderLanguageSelector = useMemo(() => {
    return (
      <nav
        className={"ac-navigation-list-wrp"}
        role={"menubar"}
        itemScope={true}
        itemType={"http://www.schema.org/SiteNavigationElement"}
      >
        <ul className={"ac-navigation-list"}>
          <li
            className="ac-navigation-list__item"
            role="presentation"
            itemProp="name"
          >
            <AcContainer className={"h-padding-left-0"}>
              <AcRow>
                <AcColumn xs={12}>
                  <span>
                    {t("select-language", "Language", { ns: "subNavigation" })}
                  </span>
                </AcColumn>
              </AcRow>
              {renderLanguageFlags}
            </AcContainer>
          </li>
        </ul>
      </nav>
    );
  }, [renderLanguageFlags]);

  const getManageAccountButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.WHITE,
      variant: VARIANTS.OUTLINE,
      size: SIZES.SMALL,
      title: `${t("manage-your-account", "Manage your account", {
        ns: "subNavigation",
      })}`,
      className: `h-margin-y-15`,
      href: `profile#details`,
    };
  }, [hasUnverifiedComplianceRequirement]);

  const renderProfileCard = useMemo(() => {
    const available =
      AcIsSet(current_merchant_profiles) &&
      AcIsObject(current_merchant_profiles) &&
      AcIsSet(current_merchant_profiles.merchant) &&
      AcIsObject(current_merchant_profiles.merchant);

    if (is_loading && !available) {
      return <AcLoaderInline loading />;
    }

    let type = TYPES.ICON;
    let value = ICONS.OPP_USER_INDIVIDUAL;

    if (available && current_merchant_profiles.merchant.type === "business") {
      value = ICONS.OPP_USER_BUSINESS;
    }

    return (
      <Fade duration={300} triggerOnce>
        <AcAvatarCard
          avatar={{ type: type, value: value }}
          title={getName}
          theme={THEMES.OMEGA}
          className={"h-white-icon-avatar-card"}
        >
          <div className={"inset-badge-button-wrapper"}>
            {(hasUnverifiedComplianceRequirement && configurations.kyc) ? (
              <span className={"inset-badge"}></span>
            ) : (
              ""
            )}

            <AcButton {...getManageAccountButtonOptions} tag={"a"}>
              <span>
                {t("merchant-account", "Merchant account", {
                  ns: "subNavigation",
                })}
              </span>
            </AcButton>
          </div>
        </AcAvatarCard>
      </Fade>
    );
  }, [
    current_merchant_profiles,
    is_loading,
    getName,
    i18n.resolvedLanguage,
    hasUnverifiedComplianceRequirement,
  ]);

  const renderHeading = useMemo(() => {
    return (
      <AcHeading
        rank={4}
        theme={THEMES.DARK}
        className={"h-margin-top-0 h-margin-bottom-20"}
      >
        <AcIconButton
          icon={ICONS.CLOSE}
          theme={THEMES.EGGWHITE}
          className={"h-margin-right-20"}
          callback={handleClose}
          flat
          title={`${t("close", "Close", { ns: "common" })}`}
        />
        {t("TITLES.ACCOUNT_AND_SETTINGS", "Account", { ns: "titles" })}
      </AcHeading>
    );
  }, [i18n.resolvedLanguage]);

  return (
    <>
      <div className={"ac-off-canvas__section h-margin-y-0"}>
        {renderHeading}
        {renderProfileCard}
      </div>

      <div
        className={
          "ac-off-canvas__section ac-off-canvas__section--flex h-padding-top-10 h-margin-top-0 h-margin-bottom-auto"
        }
      >
        {renderSecurityNavigation}
        {renderSupportNavigation}
        {renderServiceNavigation}
        {renderLanguageSelector}
      </div>
    </>
  );
};

export default withTranslation(["subNavigation", "common", "titles", "store"])(
  withStore(observer(AcSubNavigation))
);
